import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Recycle, UserPlus, Eye, EyeOff, Check, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const Register = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company_name: '', phone: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Klaida', description: 'Slaptažodžiai nesutampa', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const result = await register({ email: formData.email, password: formData.password, company_name: formData.company_name, phone: formData.phone });
      if (result?.requiresApproval) {
        setRegisteredEmail(formData.email);
        setRegistered(true);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast({ title: 'Klaida', description: error.response?.data?.detail || 'Registracijos klaida', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const benefits = ['Nemokamas naudojimas', 'PDF ataskaitos', 'Kelių metų prognozė', 'Organizacijų palyginimas'];

  // Show success/pending approval screen
  if (registered) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Recycle className="w-8 h-8 text-[#00C300]" />
              <span className="text-lg font-bold text-gray-900">taršosmokestis.lt</span>
            </Link>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md text-center">
            <div className="inline-flex w-20 h-20 bg-yellow-100 rounded-full items-center justify-center mb-6">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-3">Registracija gauta!</h1>
            <p className="text-gray-600 text-lg mb-2">
              Jūsų paskyra (<strong>{registeredEmail}</strong>) sėkmingai sukurta.
            </p>
            <p className="text-gray-500 mb-8">
              Prieiga bus suteikta kai administratorius patvirtins jūsų paskyrą. 
              Gausite pranešimą el. paštu.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8 text-sm text-yellow-800">
              <strong>Ką daryti toliau?</strong><br />
              Palaukite administratoriaus patvirtinimo ir tuomet galėsite prisijungti adresu <strong>tarsosmokestis.lt/login</strong>
            </div>
            <Link to="/login">
              <Button className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-8 font-bold">
                Eiti į prisijungimą
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-[#00C300]" />
            <span className="text-lg font-bold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Jau turite paskyrą?</span>
            <Link to="/login">
              <Button variant="outline" className="rounded-full px-6 font-semibold border-2 border-gray-200 hover:border-[#00C300] hover:text-[#00C300]">Prisijungti</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div className="hidden lg:block space-y-8">
            <div>
              <h1 className="text-4xl font-extrabold text-gray-900 mb-4">Pradėkite nemokamai</h1>
              <p className="text-gray-600 text-lg leading-relaxed">Sukurkite paskyrą ir gaukite prieigą prie visų skaičiuotuvo funkcijų.</p>
            </div>
            <div className="space-y-4">
              {benefits.map(b => (
                <div key={b} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#00C300]" />
                  </div>
                  <span className="font-semibold text-gray-800">{b}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <Card className="bg-white rounded-2xl border border-gray-100 shadow-lg">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-extrabold text-gray-900">Sukurti paskyrą</h2>
                <p className="text-gray-500 text-sm mt-1">Užpildykite formą žemiau</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  { name: 'company_name', label: 'Įmonės pavadinimas', type: 'text', placeholder: 'UAB Jūsų Įmonė' },
                  { name: 'email', label: 'El. paštas', type: 'email', placeholder: 'jusu@imone.lt' },
                  { name: 'phone', label: 'Telefonas (neprivaloma)', type: 'tel', placeholder: '+370 600 00000' },
                ].map(f => (
                  <div key={f.name} className="space-y-1.5">
                    <Label className="text-sm font-semibold text-gray-700">{f.label}</Label>
                    <Input type={f.type} name={f.name} placeholder={f.placeholder} value={formData[f.name]}
                      onChange={handleChange} required={f.name !== 'phone'}
                      className="h-11 rounded-xl border-gray-200 focus:border-[#00C300]" />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">Slaptažodis</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} name="password" placeholder="Min. 8 simboliai"
                      value={formData.password} onChange={handleChange} required minLength={8}
                      className="h-11 rounded-xl border-gray-200 focus:border-[#00C300] pr-12" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">Pakartoti slaptažodį</Label>
                  <Input type="password" name="confirmPassword" placeholder="Pakartokite slaptažodį"
                    value={formData.confirmPassword} onChange={handleChange} required
                    className="h-11 rounded-xl border-gray-200 focus:border-[#00C300]" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#00C300] hover:bg-[#00A300] text-white rounded-full font-bold text-base mt-2">
                  <UserPlus className="w-5 h-5 mr-2" />
                  {loading ? 'Registruojama...' : 'Registruotis nemokamai'}
                </Button>
              </form>
              <p className="text-center text-gray-500 text-sm mt-4">
                Jau turite paskyrą?{' '}
                <Link to="/login" className="text-[#00C300] font-semibold hover:underline">Prisijungti</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
